// App.js — locks header + add-customer + left list; only inventory scrolls
import { useCallback, useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import './App.css';
import logo from './logo.png';
import { supabase } from './supabaseclient';

const COLORS = ['Red', 'Orange', 'Yellow', 'Green', 'Blue', 'White'];
const ADD_CUSTOMER_URL = 'https://ggwxrbocvnivpnwxmyvc.functions.supabase.co/add-customer';
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

export default function App() {
  const [session, setSession] = useState(undefined);
  const [customers, setCustomers] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });

  const [newInventory, setNewInventory] = useState({
    animal_name: '',
    cane_tab_name: '',
    tab_color: '',
    quantity: '',
    tank: '',
    canister: '',
  });

  const [addingInventory, setAddingInventory] = useState(false);

  // Transfer UI
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferRow, setTransferRow] = useState(null); // inventory row
  const [transferSearch, setTransferSearch] = useState('');
  const [transferTargetId, setTransferTargetId] = useState(null);
  const [transferQty, setTransferQty] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Invoicing UI
  const [showInvModal, setShowInvModal] = useState(false);
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [generating, setGenerating] = useState(false);
  const [latestStatus, setLatestStatus] = useState(null);
  const [scope, setScope] = useState('all'); // 'all' | 'selected'
  const [modalSearch, setModalSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Invoice filtering + history
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [selectedInvoicePeriod, setSelectedInvoicePeriod] = useState('current');
  const [invoicePeriods, setInvoicePeriods] = useState([]);
  const [customerStatuses, setCustomerStatuses] = useState({});
  const [invoiceHistory, setInvoiceHistory] = useState([]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => listener.subscription.unsubscribe();
  }, []);

  const isAdmin = session?.user?.email === 'triplewgenetics@gmail.com';

  useEffect(() => {
    if (isAdmin) document.body.classList.add('admin');
    else document.body.classList.remove('admin');
  }, [isAdmin]);

  const fetchCustomers = useCallback(async () => {
    const { data, error } = await supabase.from('customers').select('*').order('name');
    if (!error) setCustomers(data || []);
  }, []);
  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchInventory = useCallback(async (customerId) => {
    if (!customerId) return;
    const { data, error } = await supabase
      .from('inventory')
      .select('*')
      .eq('customer_id', customerId)
      .order('animal_name', { ascending: true });
    if (!error) setInventoryData(prev => ({ ...prev, [customerId]: data || [] }));
  }, []);

  const fetchLatestInvoiceStatus = useCallback(async (cid) => {
    if (!cid) return setLatestStatus(null);
    const { data } = await supabase
      .from('invoices')
      .select('status, amount_cents, start_month, end_month, created_at')
      .eq('customer_id', cid)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setLatestStatus(data || null);
  }, []);
  useEffect(() => { fetchLatestInvoiceStatus(currentCustomerId); }, [currentCustomerId, fetchLatestInvoiceStatus]);

  const fetchAllInvoiceStatuses = useCallback(async () => {
    const { data } = await supabase
      .from('invoices')
      .select('customer_id, status, start_month, end_month, created_at')
      .order('created_at', { ascending: false });

    const map = {};
    (data || []).forEach(inv => {
      const cid = inv.customer_id;
      const status = (inv.status || '').toLowerCase();
      const start = inv.start_month;
      const end = inv.end_month;
      const periodKey = start && end ? `${start}__${end}` : null;

      if (!map[cid]) {
        map[cid] = {
          current: null,
          periods: {},
        };
      }

      if (!map[cid].current) {
        map[cid].current = status;
      }

      if (periodKey && !map[cid].periods[periodKey]) {
        map[cid].periods[periodKey] = status;
      }
    });

    setCustomerStatuses(map);
  }, []);

  useEffect(() => {
    fetchAllInvoiceStatuses();
  }, [fetchAllInvoiceStatuses]);

  const fetchInvoicePeriods = useCallback(async () => {
    const { data } = await supabase
      .from('invoices')
      .select('start_month, end_month')
      .order('start_month', { ascending: false });

    const seen = new Set();
    const periods = [];

    (data || []).forEach(inv => {
      const start = inv.start_month;
      const end = inv.end_month;
      if (!start || !end) return;

      const key = `${start}__${end}`;
      if (seen.has(key)) return;
      seen.add(key);

      periods.push({
        key,
        start,
        end,
        label: `${start} to ${end}`,
      });
    });

    setInvoicePeriods(periods);
  }, []);

  useEffect(() => {
    fetchInvoicePeriods();
  }, [fetchInvoicePeriods]);

  const fetchInvoiceHistory = useCallback(async (cid) => {
    if (!cid) {
      setInvoiceHistory([]);
      return;
    }

    const { data } = await supabase
      .from('invoices')
      .select('*')
      .eq('customer_id', cid)
      .order('created_at', { ascending: false });

    setInvoiceHistory(data || []);
  }, []);

  useEffect(() => {
    fetchInvoiceHistory(currentCustomerId);
  }, [currentCustomerId, fetchInvoiceHistory]);

  // Auto-select own record for regular users
  useEffect(() => {
    const setCustomerForUser = async () => {
      if (!session || isAdmin) return;
      const email = session.user.email;
      const { data } = await supabase.from('customers').select('id').eq('email', email).limit(1).maybeSingle();
      const id = data?.id || null;
      if (id) { setCurrentCustomerId(id); fetchInventory(id); }
    };
    setCustomerForUser();
  }, [session, isAdmin, fetchInventory]);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase.functions.invoke('add-customer', {
        body: {
          name: newCustomer.name,
          email: newCustomer.email,
          phone: newCustomer.phone,
        },
      });
      if (error) throw new Error(error.message || 'Add failed');
      if (!data?.ok && !data?.customer) throw new Error(data?.error || 'Add failed');

      setNewCustomer({ name: '', phone: '', email: '' });
      await fetchCustomers();

      alert(data?.mode === 'recovery' ? 'Customer added. Reset email sent.' : 'Customer added. Invite email sent.');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleAddInventory = async (customerId) => {
    if (!newInventory.animal_name || !newInventory.quantity) return;
    setAddingInventory(true);

    const payload = {
      animal_name: newInventory.animal_name,
      cane_tab_name: newInventory.cane_tab_name,
      tab_color: newInventory.tab_color,
      quantity: newInventory.quantity,
      tank: newInventory.tank,
      canister: newInventory.canister,
      customer_id: customerId,
    };

    const { error } = await supabase.from('inventory').insert([payload]);
    setAddingInventory(false);
    if (!error) {
      setNewInventory({
        animal_name: '',
        cane_tab_name: '',
        tab_color: '',
        quantity: '',
        tank: '',
        canister: '',
      });
      fetchInventory(customerId);
    }
  };

  const handleInventoryChange = async (id, field, value) => {
    setInventoryData(prev => {
      const updated = { ...prev };
      for (const key in updated) {
        updated[key] = updated[key].map(row => row.id === id ? { ...row, [field]: value } : row);
      }
      return updated;
    });
    await supabase.from('inventory').update({ [field]: value }).eq('id', id);
  };

  const handleDeleteInventory = async (id, customerId) => {
    await supabase.from('inventory').delete().eq('id', id);
    fetchInventory(customerId);
  };

  const openTransferModal = (row) => {
    setTransferRow(row);
    setTransferSearch('');
    setTransferTargetId(null);
    setTransferQty('');
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    if (transferring) return;
    setShowTransferModal(false);
    setTransferRow(null);
    setTransferSearch('');
    setTransferTargetId(null);
    setTransferQty('');
  };

  const submitTransfer = async () => {
    if (!isAdmin) return;
    if (!transferRow?.id) return;
    if (!transferTargetId) return alert('Pick a customer.');
    if (transferTargetId === currentCustomerId) return alert('That entry is already in this customer.');

    const currentQty = Number(transferRow.quantity || 0);
    const qtyToMove = Number(transferQty);

    if (!Number.isFinite(qtyToMove) || qtyToMove <= 0) return alert('Enter a valid quantity.');
    if (qtyToMove > currentQty) return alert('Transfer quantity can’t be more than current quantity.');

    setTransferring(true);

    // Case A: move entire row
    if (qtyToMove === currentQty) {
      const rowToMove = transferRow;

      setInventoryData(prev => {
        const next = { ...prev };

        const curList = Array.isArray(next[currentCustomerId]) ? next[currentCustomerId] : [];
        next[currentCustomerId] = curList.filter(r => r.id !== rowToMove.id);

        if (Array.isArray(next[transferTargetId])) {
          next[transferTargetId] = [{ ...rowToMove, customer_id: transferTargetId }, ...next[transferTargetId]];
        }

        return next;
      });

      const { error } = await supabase
        .from('inventory')
        .update({ customer_id: transferTargetId })
        .eq('id', rowToMove.id);

      setTransferring(false);

      if (error) {
        await fetchInventory(currentCustomerId);
        await fetchInventory(transferTargetId);
        return alert(error.message || 'Transfer failed');
      }

      await fetchInventory(currentCustomerId);
      await fetchInventory(transferTargetId);

      closeTransferModal();
      return;
    }

    // Case B: split row (partial transfer)
    const remainingQty = currentQty - qtyToMove;

    // optimistic UI
    setInventoryData(prev => {
      const next = { ...prev };

      const curList = Array.isArray(next[currentCustomerId]) ? next[currentCustomerId] : [];
      next[currentCustomerId] = curList.map(r =>
        r.id === transferRow.id ? { ...r, quantity: remainingQty } : r
      );

      if (Array.isArray(next[transferTargetId])) {
        const tempId = `tmp_${Date.now()}`;
        const newRow = {
          ...transferRow,
          id: tempId,
          customer_id: transferTargetId,
          quantity: qtyToMove,
        };
        next[transferTargetId] = [newRow, ...next[transferTargetId]];
      }

      return next;
    });

    const { error: updErr } = await supabase
      .from('inventory')
      .update({ quantity: remainingQty })
      .eq('id', transferRow.id);

    if (updErr) {
      setTransferring(false);
      await fetchInventory(currentCustomerId);
      await fetchInventory(transferTargetId);
      return alert(updErr.message || 'Transfer failed');
    }

    const insertPayload = {
      customer_id: transferTargetId,
      animal_name: transferRow.animal_name || '',
      cane_tab_name: transferRow.cane_tab_name || '',
      tab_color: transferRow.tab_color || '',
      tank: transferRow.tank || '',
      canister: transferRow.canister || '',
      quantity: qtyToMove,
    };

    const { error: insErr } = await supabase.from('inventory').insert([insertPayload]);

    setTransferring(false);

    if (insErr) {
      await fetchInventory(currentCustomerId);
      await fetchInventory(transferTargetId);
      return alert(insErr.message || 'Transfer failed');
    }

    await fetchInventory(currentCustomerId);
    await fetchInventory(transferTargetId);

    closeTransferModal();
  };

  const filteredCustomers = useMemo(() => {
    return customers
      .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter(c => {
        const customerInvoiceData = customerStatuses[c.id];
        const status = selectedInvoicePeriod === 'current'
          ? customerInvoiceData?.current
          : customerInvoiceData?.periods?.[selectedInvoicePeriod];

        if (invoiceFilter === 'all') return true;
        if (invoiceFilter === 'not_invoiced') return !status;
        return status === invoiceFilter;
      });
  }, [customers, searchTerm, invoiceFilter, customerStatuses, selectedInvoicePeriod]);

  const currentCustomer = customers.find(c => c.id === c.id && c.id === currentCustomerId) || null;

  const monthDiffIncl = (a, b) => {
    const [ay, am] = a.split('-').map(Number), [by, bm] = b.split('-').map(Number);
    return (by - ay) * 12 + (bm - am) + 1;
  };

  const submitGenerate = async () => {
    if (!startMonth || !endMonth) return alert('Select both months.');
    if (endMonth < startMonth) return alert('End month can’t be before start.');
    if (scope === 'selected' && selectedIds.size === 0) return alert('Pick at least one customer.');

    setGenerating(true);
    const customerIds = scope === 'selected' ? Array.from(selectedIds) : null;
    const { data, error } = await supabase.functions.invoke('generate-invoices', {
      body: { startMonth, endMonth, customerIds }
    });
    setGenerating(false);
    if (error) return alert(error.message || 'Failed');
    setShowInvModal(false);
    alert(`Invoices queued: ${data?.created || 0}`);
    fetchLatestInvoiceStatus(currentCustomerId);
    fetchAllInvoiceStatuses();
    fetchInvoiceHistory(currentCustomerId);
    fetchInvoicePeriods();
    setSelectedIds(new Set());
  };

  const modalCandidates = useMemo(() => {
    const q = modalSearch.toLowerCase();
    return customers.filter(c => c.name.toLowerCase().includes(q));
  }, [customers, modalSearch]);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const transferCandidates = useMemo(() => {
    const q = transferSearch.toLowerCase();
    return customers
      .filter(c => c.id !== currentCustomerId)
      .filter(c => c.name.toLowerCase().includes(q));
  }, [customers, transferSearch, currentCustomerId]);

  return (
    <div className="app-container">
      {/* Header + sign out */}
      <header className="header">
        <img src={logo} alt="Logo" className="logo" />
        <h1>Inventory Database</h1>
        <div className="sub">{session?.user?.email || ''}</div>

        {isAdmin && (
          <button className="btn-secondary" onClick={() => setShowInvModal(true)}>
            Generate invoices
          </button>
        )}

        <button className="signout-btn" onClick={() => supabase.auth.signOut()}>Sign out</button>
      </header>

      {/* Add Customer — always visible for admin */}
      {isAdmin && (
        <form className="add-customer-form admin-only" onSubmit={handleAddCustomer}>
          <input className="input" type="text" placeholder="Name" value={newCustomer.name}
                required onChange={e=>setNewCustomer({...newCustomer, name:e.target.value})}/>
          <InputMask className="input" mask="999-999-9999" placeholder="Phone" value={newCustomer.phone}
                    required onChange={e=>setNewCustomer({...newCustomer, phone:e.target.value})}/>
          <input className="input" type="email" placeholder="Email" value={newCustomer.email}
                required onChange={e=>setNewCustomer({...newCustomer, email:e.target.value})}/>
          <button className="btn-primary" type="submit">Add Customer</button>
        </form>
      )}

      {/* CONTENT */}
      <main className="content">
        {!currentCustomerId && (
          <div className="centered-layout">
            <input
              className="search-bar admin-only"
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />

            {isAdmin && (
  <div className="filter-row admin-only">
    <select
      className="input invoice-filter"
      value={invoiceFilter}
      onChange={e => setInvoiceFilter(e.target.value)}
    >
      <option value="all">All Customers</option>
      <option value="not_invoiced">Not Invoiced</option>
      <option value="paid">Paid</option>
      <option value="unpaid">Unpaid</option>
    </select>

    <select
      className="input invoice-filter"
      value={selectedInvoicePeriod}
      onChange={e => setSelectedInvoicePeriod(e.target.value)}
    >
      <option value="current">Current</option>
      {invoicePeriods.map(period => (
        <option key={period.key} value={period.key}>
          {period.label}
        </option>
      ))}
    </select>
  </div>
)}
            <div className="customer-list admin-only">
              {filteredCustomers.map(c => {
                const displayStatus = selectedInvoicePeriod === 'current'
                  ? customerStatuses[c.id]?.current
                  : customerStatuses[c.id]?.periods?.[selectedInvoicePeriod];

                return (
                  <div
                    key={c.id}
                    className="customer-card"
                    onClick={() => { setCurrentCustomerId(c.id); fetchInventory(c.id); }}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
                  >
                    <span className="name">{c.name}</span>
                    <span className="status-pill" style={{ fontSize: 12, padding: '4px 8px' }}>
                      {displayStatus ? displayStatus.toUpperCase() : 'NONE'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {currentCustomerId && (
          <div className="two-column">
            <div className="left-panel admin-only">
              <input
                className="search-bar admin-only"
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />

              {isAdmin && (
  <div className="filter-row admin-only">
    <select
      className="input invoice-filter"
      value={invoiceFilter}
      onChange={e => setInvoiceFilter(e.target.value)}
    >
      <option value="all">All Customers</option>
      <option value="not_invoiced">Not Invoiced</option>
      <option value="paid">Paid</option>
      <option value="unpaid">Unpaid</option>
    </select>

    <select
      className="input invoice-filter"
      value={selectedInvoicePeriod}
      onChange={e => setSelectedInvoicePeriod(e.target.value)}
    >
      <option value="current">Current</option>
      {invoicePeriods.map(period => (
        <option key={period.key} value={period.key}>
          {period.label}
        </option>
      ))}
    </select>
  </div>
)}
              <div className="customer-list">
                {filteredCustomers.map(c => {
                  const displayStatus = selectedInvoicePeriod === 'current'
                    ? customerStatuses[c.id]?.current
                    : customerStatuses[c.id]?.periods?.[selectedInvoicePeriod];

                  return (
                    <div
                      key={c.id}
                      className={`customer-card ${c.id === currentCustomerId ? 'active' : ''}`}
                      onClick={() => { setCurrentCustomerId(c.id); fetchInventory(c.id); }}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
                    >
                      <span className="name">{c.name}</span>
                      <span className="status-pill" style={{ fontSize: 12, padding: '4px 8px' }}>
                        {displayStatus ? displayStatus.toUpperCase() : 'NONE'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="right-panel">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,marginBottom:10}}>
                <button className="back-btn admin-only" onClick={() => setCurrentCustomerId(null)}>
                  Back to customer list
                </button>
                <div className="inventory-title" style={{marginLeft:'auto',textAlign:'right'}}>
                  <div className="name">{currentCustomer?.name || 'Inventory'}</div>
                  <div className="contact">
                    {currentCustomer?.phone || ''}{currentCustomer?.phone && currentCustomer?.email ? ' • ' : ''}{currentCustomer?.email || ''}
                  </div>
                  {latestStatus && (
                    <div className="status-pill" title={`Last invoice ${new Date(latestStatus.created_at).toLocaleDateString()}`}>
                      {latestStatus.status?.toUpperCase() || 'UNKNOWN'}
                    </div>
                  )}
                </div>
              </div>

              {invoiceHistory.length > 0 && (
                <div className="invoice-history">
                  <div className="invoice-history-title">Invoice History</div>
                  <div className="invoice-history-list">
                    {invoiceHistory.map(inv => (
                      <div key={inv.id} className="invoice-row">
                        <span>{inv.start_month} to {inv.end_month}</span>
                        <span>${((inv.amount_cents || 0) / 100).toFixed(2)}</span>
                        <span>{(inv.status || 'unknown').toUpperCase()}</span>
                        <span>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="add-inventory-form admin-only">
                  <input className="input" type="text" placeholder="Animal Name" value={newInventory.animal_name}
                        onChange={e=>setNewInventory({...newInventory, animal_name:e.target.value})}/>
                  <input className="input" type="text" placeholder="Cane Tab" value={newInventory.cane_tab_name}
                        onChange={e=>setNewInventory({...newInventory, cane_tab_name:e.target.value})}/>

                  <input className="input" type="text" placeholder="Tank" value={newInventory.tank}
                        onChange={e=>setNewInventory({...newInventory, tank:e.target.value})}/>
                  <input className="input" type="text" placeholder="Canister" value={newInventory.canister}
                        onChange={e=>setNewInventory({...newInventory, canister:e.target.value})}/>

                  <select className="input" value={newInventory.tab_color}
                          onChange={e=>setNewInventory({...newInventory, tab_color:e.target.value})}>
                    <option value="">Tab Color</option>
                    {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="input" type="number" placeholder="Quantity" value={newInventory.quantity}
                        onChange={e=>setNewInventory({...newInventory, quantity:e.target.value})}/>
                  <button className="btn-primary" onClick={()=>handleAddInventory(currentCustomerId)}
                          disabled={addingInventory} type="button">
                    {addingInventory ? 'Adding…' : 'Add Entry'}
                  </button>
                </div>
              )}

              <div className="table-wrap">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Animal Name</th>
                      <th>Cane Tab</th>
                      <th>Tank</th>
                      <th>Canister</th>
                      <th>Tab Color</th>
                      <th>Quantity</th>
                      {isAdmin && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(inventoryData[currentCustomerId] || []).map(item => (
                      <tr key={item.id}>
                        <td>{isAdmin ? (
                          <input className="table-input" value={item.animal_name || ''}
                                onChange={e => handleInventoryChange(item.id,'animal_name',e.target.value)} />
                        ) : <span>{item.animal_name || ''}</span>}</td>

                        <td>{isAdmin ? (
                          <input className="table-input" value={item.cane_tab_name || ''}
                                onChange={e => handleInventoryChange(item.id,'cane_tab_name',e.target.value)} />
                        ) : <span>{item.cane_tab_name || ''}</span>}</td>

                        <td>{isAdmin ? (
                          <input className="table-input" value={item.tank || ''}
                                onChange={e => handleInventoryChange(item.id,'tank',e.target.value)} />
                        ) : <span>{item.tank || ''}</span>}</td>

                        <td>{isAdmin ? (
                          <input className="table-input" value={item.canister || ''}
                                onChange={e => handleInventoryChange(item.id,'canister',e.target.value)} />
                        ) : <span>{item.canister || ''}</span>}</td>

                        <td>{isAdmin ? (
                          <select className="table-input" value={item.tab_color || ''}
                                  onChange={e => handleInventoryChange(item.id,'tab_color',e.target.value)}>
                            <option value="">None</option>
                            {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : <span>{item.tab_color || ''}</span>}</td>

                        <td>{isAdmin ? (
                          <input className="table-input" type="number" value={item.quantity || 0}
                                onChange={e => handleInventoryChange(item.id,'quantity',e.target.value)} />
                        ) : <span>{item.quantity || 0}</span>}</td>

                        {isAdmin && (
                          <td className="actions">
                            <button
                              className="btn btn-icon"
                              onClick={() => openTransferModal(item)}
                              title="Transfer"
                              type="button"
                            >
                              ⇄
                            </button>
                            <button className="btn btn-icon btn-danger"
                                    onClick={() => handleDeleteInventory(item.id, currentCustomerId)}
                                    title="Delete"
                                    type="button"
                            >
                              ❌
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        )}
      </main>

      {/* Transfer Modal */}
      {isAdmin && showTransferModal && (
        <div className="modal-backdrop" onClick={closeTransferModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Transfer Entry</h3>

            <div className="muted" style={{ marginBottom: 8 }}>
              Move <b>{transferRow?.animal_name || 'entry'}</b> to another customer.
            </div>

            <input
              className="input"
              placeholder="Search customers…"
              value={transferSearch}
              onChange={e => setTransferSearch(e.target.value)}
              disabled={transferring}
            />

            <input
              className="input"
              type="number"
              placeholder={`Qty to transfer (max ${Number(transferRow?.quantity || 0)})`}
              value={transferQty}
              onChange={e => setTransferQty(e.target.value)}
              disabled={transferring}
              style={{ marginTop: 10 }}
            />

            <div
              style={{
                maxHeight: 260,
                overflow: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: 8,
                marginTop: 10,
                display: 'grid',
                gap: 6
              }}
            >
              {transferCandidates.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTransferTargetId(c.id)}
                  disabled={transferring}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: transferTargetId === c.id ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  {c.name}
                </button>
              ))}
              {transferCandidates.length === 0 && (
                <div className="muted">No matches.</div>
              )}
            </div>

            <div className="modal-actions">
              <button disabled={transferring} onClick={closeTransferModal} type="button">Cancel</button>
              <button
                className="btn-primary"
                disabled={transferring || !transferTargetId || !Number(transferQty)}
                onClick={submitTransfer}
                type="button"
              >
                {transferring ? 'Transferring…' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Invoices Modal (All or Selected customers) */}
      {isAdmin && showInvModal && (
        <div className="modal-backdrop" onClick={() => !generating && setShowInvModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Generate Invoices</h3>

            <label>Start month</label>
            <input type="month" value={startMonth} onChange={e => setStartMonth(e.target.value)} />

            <label>End month</label>
            <input type="month" value={endMonth} onChange={e => setEndMonth(e.target.value)} />

            <div className="muted" style={{marginBottom:8}}>
              {startMonth && endMonth && endMonth >= startMonth
                ? `${monthDiffIncl(startMonth, endMonth)} month(s)`
                : 'Pick a valid range'}
            </div>

            <div style={{display:'flex',gap:12,alignItems:'center',marginTop:4}}>
              <label><input type="radio" name="scope" checked={scope==='all'} onChange={()=>setScope('all')} /> All customers</label>
              <label><input type="radio" name="scope" checked={scope==='selected'} onChange={()=>setScope('selected')} /> Selected customers</label>
            </div>

            {scope === 'selected' && (
              <div>
                <input
                  className="input"
                  placeholder="Search customers…"
                  value={modalSearch}
                  onChange={e => setModalSearch(e.target.value)}
                />
                <div className="modal-customer-list">
  {modalCandidates.map(c => (
    <label key={c.id} className="modal-customer-row">
      <input
        type="checkbox"
        checked={selectedIds.has(c.id)}
        onChange={() => toggleSelect(c.id)}
      />
      <span className="modal-customer-name">{c.name}</span>
    </label>
  ))}
</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() =>
                      setSelectedIds(new Set(modalCandidates.map(c => c.id)))
                    }
                  >
                    Select visible
                  </button>
                  <button onClick={() => setSelectedIds(new Set())}>Clear</button>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button disabled={generating} onClick={() => setShowInvModal(false)} type="button">Cancel</button>
              <button className="btn-primary" disabled={generating} onClick={submitGenerate} type="button">
                {generating ? 'Generating…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}